import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Editor from "@monaco-editor/react";
import {
  ArrowLeft, ArrowRight, Play, RotateCcw, Loader2, Sparkles,
  Calculator, ListChecks, IndianRupee, Trophy, Code2, Layers, Share2, Check
} from "lucide-react";
import { ensurePyodide, runCode } from "../lib/pyodideRunner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICON_MAP = {
  "calculator": Calculator,
  "list-checks": ListChecks,
  "indian-rupee": IndianRupee,
  "trophy": Trophy,
};

export default function ProjectShowcaseDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("# loading...\n");
  const [output, setOutput] = useState({ stdout: "", stderr: "", error: null });
  const [running, setRunning] = useState(false);
  const [pyodideStatus, setPyodideStatus] = useState("loading");
  const [shared, setShared] = useState(false);
  const editorRef = useRef(null);

  // Pyodide init
  useEffect(() => {
    setPyodideStatus("loading");
    ensurePyodide(setPyodideStatus).then(() => setPyodideStatus("ready")).catch(() => setPyodideStatus("error"));
  }, []);

  // Load project
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/python-lab/showcase/projects/${projectId}`)
      .then((r) => {
        setProject(r.data);
        setCode(r.data.demo_code || "");
        document.title = `${r.data.name} — Try Live | Python Lab`;
      })
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleRun = useCallback(async () => {
    if (pyodideStatus !== "ready") return;
    setRunning(true);
    setOutput({ stdout: "", stderr: "", error: null });
    try {
      const r = await runCode(code);
      setOutput(r);
    } finally {
      setRunning(false);
    }
  }, [code, pyodideStatus]);

  const handleReset = () => {
    if (project?.demo_code) setCode(project.demo_code);
    setOutput({ stdout: "", stderr: "", error: null });
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${project.name} — Python Lab`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch {/* user cancelled */}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
      </div>
    );
  }
  if (!project) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6">
        <p className="text-zinc-400 font-dm-sans mb-4">Project not found</p>
        <button onClick={() => navigate("/showcase/python-lab")} className="text-[#00F0FF] font-dm-sans">← Back to Showcase</button>
      </div>
    );
  }

  const Icon = ICON_MAP[project.icon] || Code2;
  const accent = project.color || "#00F0FF";

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/85 backdrop-blur-xl flex-shrink-0">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <button
            data-testid="back-to-showcase"
            onClick={() => navigate("/showcase/python-lab")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors flex-shrink-0"
          >
            <ArrowLeft size={16} /> Showcase
          </button>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="share-btn"
              onClick={handleShare}
              className="hidden sm:inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 px-3 py-1.5 rounded-md text-xs font-dm-sans font-bold transition-colors"
            >
              {shared ? <><Check size={12} /> Copied!</> : <><Share2 size={12} /> Share</>}
            </button>
            <button
              data-testid="open-lab-btn"
              onClick={() => navigate("/ai-tools/python-lab")}
              className="inline-flex items-center gap-1.5 bg-white text-black hover:bg-zinc-100 px-4 py-1.5 rounded-md text-xs font-dm-sans font-bold transition-colors"
            >
              <Sparkles size={12} /> Open Python Lab
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-900 bg-gradient-to-br from-zinc-950 via-[#050505] to-zinc-950">
        <div className="max-w-[1600px] mx-auto px-6 py-8 flex flex-col md:flex-row md:items-center gap-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${accent}1A`, color: accent }}
          >
            <Icon size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] font-mono font-bold text-zinc-500 bg-zinc-900 px-2 py-0.5 rounded">
                PROJECT · {project.step_count} STEPS
              </span>
              {project.skills.slice(0, 4).map((s) => (
                <span key={s} className="text-[10px] font-dm-sans text-zinc-300 bg-zinc-900 px-2 py-0.5 rounded">
                  {s}
                </span>
              ))}
            </div>
            <h1 className="font-outfit font-black text-3xl text-white">{project.name}</h1>
            <p className="text-zinc-400 text-sm font-dm-sans mt-1.5 max-w-3xl">{project.summary}</p>
          </div>
        </div>
      </section>

      {/* Editor + output */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* LEFT: editor */}
        <div className="lg:w-[60%] flex flex-col bg-[#0b0d12] min-h-0 lg:border-r border-zinc-900">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-900 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-zinc-500">{project.project_id}.py</span>
              {pyodideStatus !== "ready" && (
                <span className="text-[10px] font-dm-sans text-amber-400 inline-flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" /> Loading Python runtime...
                </span>
              )}
              {pyodideStatus === "ready" && (
                <span className="text-[10px] font-dm-sans text-emerald-400">● Python 3.12 ready</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="reset-demo-btn"
                onClick={handleReset}
                className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white px-3 py-1.5 rounded text-xs font-dm-sans font-bold transition-colors"
              >
                <RotateCcw size={12} /> Reset
              </button>
              <button
                data-testid="run-demo-btn"
                onClick={handleRun}
                disabled={running || pyodideStatus !== "ready"}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded text-xs font-dm-sans font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: accent, color: "#000" }}
              >
                {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                Run
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0" style={{ minHeight: "400px" }}>
            <Editor
              height="100%"
              language="python"
              theme="vs-dark"
              value={code}
              onChange={(v) => setCode(v ?? "")}
              onMount={(ed) => { editorRef.current = ed; }}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: 'JetBrains Mono, Menlo, monospace',
                tabSize: 4,
                insertSpaces: true,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                wordWrap: "on",
                lineNumbers: "on",
              }}
            />
          </div>
        </div>

        {/* RIGHT: output + steps + CTA */}
        <div className="lg:w-[40%] flex flex-col bg-[#070709] min-h-0">
          {/* Output */}
          <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
            {/* Steps */}
            <div>
              <div className="text-[10px] font-mono text-zinc-500 mb-2">PROJECT STEPS</div>
              <ol className="space-y-1.5">
                {project.steps.map((s) => (
                  <li key={s.step_number} className="flex items-start gap-3 text-sm font-dm-sans">
                    <span
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono font-bold mt-0.5"
                      style={{ backgroundColor: `${accent}25`, color: accent }}
                    >
                      {s.step_number}
                    </span>
                    <span className="text-zinc-300">{s.title}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Output panel */}
            <div>
              <div className="text-[10px] font-mono text-zinc-500 mb-2">OUTPUT</div>
              <pre className="bg-black/50 border border-zinc-900 rounded p-3 text-xs font-mono text-zinc-200 whitespace-pre-wrap min-h-32 max-h-[40vh] overflow-y-auto leading-relaxed">
                {output.stdout && <span className="text-zinc-200">{output.stdout}</span>}
                {output.stderr && <span className="text-amber-300">{"\n" + output.stderr}</span>}
                {output.error && <span className="text-red-400">{"\n" + output.error}</span>}
                {!output.stdout && !output.stderr && !output.error && (
                  <span className="text-zinc-600">{"// Click "}<span style={{ color: accent }}>Run</span>{" to execute the code on the left.\n// Try changing values, adding new lines, or breaking it on purpose."}</span>
                )}
              </pre>
            </div>
          </div>

          {/* Sign up CTA */}
          <div className="border-t border-zinc-900 p-5 bg-zinc-950 flex-shrink-0">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-outfit font-bold text-white text-sm">Want to build this yourself?</div>
                <div className="text-xs text-zinc-400 font-dm-sans mt-0.5 leading-snug">Python Lab walks you through each step with auto-graded tests + an AI tutor.</div>
                <button
                  data-testid="cta-open-lab"
                  onClick={() => navigate("/ai-tools/python-lab")}
                  className="mt-3 inline-flex items-center gap-1.5 bg-white text-black hover:bg-zinc-100 px-4 py-1.5 rounded-md text-xs font-dm-sans font-bold transition-colors"
                >
                  Open Python Lab <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
